import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, RefreshCw, Trash2, ShieldCheck, Lock, Clock, Copy, Inbox, ChevronRight, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';

const ICONS = {
  sin_evidencia: Inbox,
  cubiertos_ros: ShieldCheck,
  cuenta_bloqueada: Lock,
  viejos_bajo_riesgo: Clock,
  reincidentes: Copy,
};
const COLORS = {
  slate: 'border-slate-300 bg-slate-50 text-slate-700',
  emerald: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  amber: 'border-amber-300 bg-amber-50 text-amber-700',
  zinc: 'border-zinc-300 bg-zinc-50 text-zinc-700',
  violet: 'border-violet-300 bg-violet-50 text-violet-700',
};

const BacklogCleanup = () => {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activa, setActiva] = useState(null); // canasta abierta
  const [casos, setCasos] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loadingCasos, setLoadingCasos] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [procesando, setProcesando] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const recalcularClave = async () => {
    if (!window.confirm('Recalcular la clave de cliente en todos los casos. Esto agrupa correctamente a los clientes (CUIT con/sin guiones). ¿Continuar?')) return;
    setBackfilling(true);
    try {
      const r = await api.post('/cases/backfill-cliente-key');
      toast.success(`Clave recalculada: ${r.data?.actualizados || 0} casos actualizados.`);
      loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo recalcular');
    } finally {
      setBackfilling(false);
    }
  };

  const loadResumen = async () => {
    setLoading(true);
    try {
      const r = await api.get('/cases/backlog/resumen');
      setResumen(r.data);
    } catch (e) {
      toast.error('No se pudo cargar el backlog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadResumen(); }, []);

  const abrirCanasta = async (key, m) => {
    if (key === 'reincidentes') {
      navigate('/m/gestion-de-casos/casos-duplicados');
      return;
    }
    setActiva(key);
    setMeta(m);
    setSelected(new Set());
    setLoadingCasos(true);
    try {
      const r = await api.get(`/cases/backlog/canasta/${key}`);
      setCasos(r.data.casos || []);
      setSelected(new Set((r.data.casos || []).map((c) => c.id))); // por defecto todos
    } catch (e) {
      toast.error('No se pudo cargar la canasta');
    } finally {
      setLoadingCasos(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const resolver = async () => {
    if (selected.size === 0) { toast.error('Seleccioná al menos un caso'); return; }
    const accion = meta.accion === 'cerrar' ? 'cerrar' : 'archivar';
    const verbo = accion === 'cerrar' ? 'cerrar' : 'archivar';
    if (!window.confirm(`¿Seguro que querés ${verbo} ${selected.size} caso(s)? Esta acción cambia su estado.`)) return;
    setProcesando(true);
    try {
      const r = await api.post('/cases/backlog/resolver', {
        canasta: activa, accion, case_ids: Array.from(selected),
      });
      toast.success(r.data?.mensaje || 'Listo');
      setActiva(null);
      setCasos([]);
      loadResumen();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al procesar');
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="backlog-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/m/gestion-de-casos/casos')} data-testid="backlog-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Poner al día el backlog</h1>
            <p className="text-sm text-slate-500">
              El sistema agrupa los casos abiertos en canastas accionables. Revisá y confirmá: nada se cierra solo.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={recalcularClave} disabled={backfilling} data-testid="backfill-clave-btn" title="Agrupa correctamente a los clientes normalizando el CUIT (con/sin guiones)">
            <RefreshCw size={14} className={`mr-1 ${backfilling ? 'animate-spin' : ''}`} /> Recalcular clave de cliente
          </Button>
          <Button variant="outline" size="sm" onClick={loadResumen} disabled={loading} data-testid="backlog-refresh-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      {resumen && (
        <div className="mb-4 text-sm text-slate-600">
          Total de casos abiertos: <strong data-testid="backlog-total-abiertos">{resumen.total_abiertos}</strong>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resumen?.canastas?.map((c) => {
          const Icon = ICONS[c.key] || Inbox;
          return (
            <Card
              key={c.key}
              className={`p-5 border-2 transition-all hover:shadow-md cursor-pointer ${COLORS[c.color] || COLORS.slate}`}
              onClick={() => abrirCanasta(c.key, c)}
              data-testid={`backlog-card-${c.key}`}
            >
              <div className="flex items-start justify-between">
                <Icon size={22} />
                <span className="text-3xl font-bold" data-testid={`backlog-count-${c.key}`}>{c.cantidad}</span>
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{c.titulo}</h3>
              <p className="text-xs text-slate-600 mt-1">{c.desc}</p>
              <div className="mt-3 flex items-center text-xs font-medium">
                {c.key === 'reincidentes'
                  ? <>Ir a Duplicados <ChevronRight size={14} /></>
                  : <>{c.accion === 'cerrar' ? 'Revisar y cerrar' : 'Revisar y archivar'} <ChevronRight size={14} /></>}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Panel de canasta */}
      {activa && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => !procesando && setActiva(null)}>
          <div className="bg-white rounded-t-xl md:rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="backlog-canasta-panel">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="font-semibold text-slate-900">{meta?.titulo}</h3>
                <p className="text-xs text-slate-500">{meta?.desc}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setActiva(null)} disabled={procesando}><X size={18} /></Button>
            </div>

            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b text-sm">
              <span>{selected.size} de {casos.length} seleccionados</span>
              <div className="flex gap-2">
                <button className="text-blue-600 hover:underline" onClick={() => setSelected(new Set(casos.map((c) => c.id)))}>Todos</button>
                <button className="text-slate-500 hover:underline" onClick={() => setSelected(new Set())}>Ninguno</button>
              </div>
            </div>

            <div className="overflow-auto flex-1 divide-y">
              {loadingCasos ? (
                <div className="p-8 text-center text-slate-400">Cargando…</div>
              ) : casos.length === 0 ? (
                <div className="p-8 text-center text-slate-400">No hay casos en esta canasta.</div>
              ) : casos.map((c) => (
                <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer" data-testid={`backlog-caso-${c.id}`}>
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.titulo}</div>
                    <div className="text-xs text-slate-500">
                      {c.cuit || '—'} · {c.total_alertas} alerta(s) · score {c.score_riesgo ?? '—'} · {c.responsable}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="p-4 border-t flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setActiva(null)} disabled={procesando}>Cancelar</Button>
              <Button
                onClick={resolver}
                disabled={procesando || selected.size === 0}
                className={meta?.accion === 'cerrar' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-slate-700 hover:bg-slate-800 text-white'}
                data-testid="backlog-resolver-btn"
              >
                {meta?.accion === 'cerrar' ? <ShieldCheck size={16} className="mr-1.5" /> : <Trash2 size={16} className="mr-1.5" />}
                {procesando ? 'Procesando…' : `${meta?.accion === 'cerrar' ? 'Cerrar' : 'Archivar'} ${selected.size} caso(s)`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacklogCleanup;
